import type { FetchQueryOptions } from "@tanstack/react-query";

import { queryClient } from "pages/Root";

/**
 * Starts a route's content requests at navigation time without making the route wait for them.
 *
 * React Router keeps the previous screen on display until every matched loader resolves, so a
 * loader that awaits its content freezes the app on the page the patient just left. `prefetchQuery`
 * issues exactly the request `fetchQuery` would — same key, same dedupe, same cache entry — it
 * simply does not resolve into the loader, so the page renders now and the sections that need the
 * data show a skeleton until it arrives.
 *
 * A route keeps awaiting the queries a routing decision turns on: which route renders, or whether
 * the patient may be here at all.
 */
export const prefetchContent = (factories: Record<string, () => FetchQueryOptions>) =>
  // Resolves once the requests have settled so a caller that wants to stage its warming can await
  // it. A rejection is the consuming `useQuery`'s to report, so this never rejects.
  Promise.allSettled(
    Object.values(factories).map((buildOptions) => queryClient.prefetchQuery(buildOptions())),
  ).then(() => undefined);
